/**
 * Utility functions for printing and generating PDFs from notes
 */

/**
 * Main function to print or download a note as PDF
 * Uses browser's built-in print functionality which allows saving as PDF
 */
export const printNote = (note, noteType, clientName) => {
  // Parse the note content
  let parsedContent;
  try {
    parsedContent = typeof note.notes === 'string' ? JSON.parse(note.notes) : note.notes;
  } catch (err) {
    console.error('Error parsing note for printing:', err);
    alert('There was an error preparing this note for printing.');
    return;
  }

  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow popups for this site to print or save as PDF.');
    return;
  }

  // Generate HTML for print based on note type
  let printContent;
  
  switch (noteType) {
    case 'SOAP Note':
      printContent = generateSOAPPrintContent(parsedContent, clientName);
      break;
    case 'TA Contract':
      printContent = generateTAContractPrintContent(parsedContent, clientName);
      break;
    case 'IntakeAssessment':
      printContent = generateIntakeAssessmentPrintContent(parsedContent, clientName);
      break;
    default:
      printContent = generateGenericPrintContent(parsedContent, clientName, noteType);
  }

  // Write the content to the new window
  printWindow.document.write(printContent);
  printWindow.document.close();
  
  // Wait for content to load before printing
  printWindow.onload = function() {
    // Add listener for after print to close the window
    printWindow.addEventListener('afterprint', function() {
      printWindow.close();
    });
    
    printWindow.print();
  };
};

/**
 * Generate print content for SOAP notes
 */
const generateSOAPPrintContent = (note, clientName) => {
  const date = note.date || new Date().toLocaleDateString();
  const sessionType = note.sessionType || 'Not specified';
  
  // Extract sections
  const subjective = note.subjective || {};
  const objective = note.objective || {};
  const assessment = note.assessment || {};
  const plan = note.plan || '';

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>SOAP Note - ${clientName}</title>
      <style>
        ${getPrintStyles()}
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="header">
          <div class="header-left">
            <h1>SOAP Note</h1>
            <div class="client-info">
              <p><strong>Client:</strong> ${clientName}</p>
              <p><strong>Date:</strong> ${date}</p>
              <p><strong>Session Type:</strong> ${sessionType}</p>
            </div>
          </div>
          <div class="header-right">
            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAC1UlEQVR4nO2ZTWhTQRDHf01qqKLFWluRIi2KH1gVD4o9KIoHFRHxIF4E9SJeRMSLePEgCIIXPyBaPQiiIIIHIaguKn4gilYrKKJYW6u1tVZrNOJhJmRNXt59+/btoZD8YAjZ2Z3578zu7M4bqKEGMlAHDAOvgR/AT2AMGAFOAtsLoF0RDACfAdfHpoCRIsiXFbuAaRnwC+A00CdaRj57ZI7TNe4u0F0wH20sBb7JoGeAw0BDytysPZfE5nIxJOxlAbBfPvsFnMsjdwJYLAJdCsOD6/eYXuAasEU8Mwk8Ak4AsxFkwLbMM8EV4PUcTVQRgYbEvZeA+4lpIBHPXAebVY4eZLGTH/K9Mc/GOWM80eBZ2DiBDTn0NQDvxObtnLZsEKcdGE5eyrP4TbH5mNNWv5xKYMNyjbPWx/XMTnuNfjCWEu7pM+tEO2LLUhGbrXsZdHeB4RyO2EATMCa2H5jtZUSUu3+pjPv2OcZZ7rRdBtZkNdjL6RQBMacxA5wE1jltzdKHpTmyHiVuiZ3fwA5nbrNsfqWpbFwRy++ABvk/T6LLZeBy1qD7JSVZ+XAeWCPrx9tOJrIdmPEksFvG9JisoNYkMO/NW0EWE4sO+jA3Yci7V+r93+jzkGuwfNJuhGttDTdNpJmKSGCJmScuBYvgLPNDXCbWIUXLQmAn3GfE4DPLF4VqQBp2m4xg63ot4WsDZgNaA8skRF455Wsu+4mCv5LVfC12Qgge9Jt+3Cr7qrVgIzAWuPnLlUKb1F9vA40vhEo3J4C7EZEG5aXwOdDbSrQJW6VAW6CKtVnC5mqgfBQMm0KWuFrM1dhm4HvVnpYL3TJwa0DzXRYkdapVjPXASwkZ95WWLbfbKs1LRNSBtoSfRYNVWhvNCVs1LVs/TkvJoG9NVBSrgbuSPH0C9mHQrJWLuaDSnSrBStc+kbCNHKp0KWuooUL4C0kZ9kKDi86wAAAAAElFTkSuQmCC" alt="Avenues Unlimited Counseling Center" />
            <p>Avenues Unlimited Counseling Center</p>
          </div>
        </div>
        <hr>
        
        <div class="section">
          <h2>S - Subjective</h2>
          <div class="subsection">
            <p><strong>Mood:</strong> ${subjective.mood || 'Not specified'}</p>
            <p><strong>Feelings/Behavior:</strong> ${subjective.feelingsExpressed || 'None recorded'}</p>
            <p><strong>Session Focus:</strong> ${subjective.sessionFocus || 'None recorded'}</p>
          </div>
        </div>
        
        <div class="section">
          <h2>O - Objective</h2>
          <div class="subsection">
            <p><strong>Interventions Used:</strong> ${objective.interventions || 'None recorded'}</p>
            <p><strong>Additional Techniques:</strong> ${objective.additionalTechniques || 'None recorded'}</p>
            <p><strong>Engagement Level:</strong> ${objective.engagementLevel || 'Not specified'}</p>
          </div>
        </div>
        
        <div class="section">
          <h2>A - Assessment</h2>
          <div class="subsection">
            <p><strong>Client Response:</strong> ${assessment.clientResponse || 'None recorded'}</p>
            <p><strong>Client Progress:</strong> ${assessment.clientProgress || 'Not specified'}</p>
          </div>
        </div>
        
        <div class="section">
          <h2>P - Plan</h2>
          <div class="subsection">
            <p>${plan || 'No plan specified'}</p>
          </div>
        </div>
        
        <div class="signature-section">
          <div class="signature-line">
            <span>Therapist Signature</span>
          </div>
          <div class="signature-line">
            <span>Date</span>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Generate print content for TA Contract
 */
const generateTAContractPrintContent = (note, clientName) => {
  const date = note.date || new Date().toLocaleDateString();
  
  // Define the questions for the TA Contract
  const questions = [
    ["endVision", "Imagine it's the end of therapy… how are you different?"],
    ["currentState", "Currently:"],
    ["desiredState", "How you would like to be:"],
    ["feelings", "What I am feeling:"],
    ["selfBeliefs", "Beliefs about myself:"],
    ["otherBeliefs", "Beliefs about others:"],
    ["actions", "What I am doing:"],
    ["enhanceLife", "What could you change about yourself that would enhance your life?"],
    ["howToAccomplish", "What would you need to do to accomplish that?"],
    ["benefit", "What about that would be beneficial or pleasing to you?"],
    ["measureSuccess", "How would you and I both know that you've gotten there?"],
    ["sabotage", "How could you sabotage your success?"],
    ["avoidSabotage", "What will you do instead to ensure success?"],
    ["loss", "What will you be letting go of when you change?"],
    ["gain", "What will you be gaining?"]
  ];

  // Generate the contract sections
  let contractSections = '';
  questions.forEach(([key, label]) => {
    contractSections += `
      <div class="contract-item">
        <h3>${label}</h3>
        <p>${note[key] || 'Not specified'}</p>
      </div>
    `;
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>TA Contract - ${clientName}</title>
      <style>
        ${getPrintStyles()}
        .contract-item {
          margin-bottom: 20px;
        }
        .contract-item h3 {
          font-size: 14px;
          margin-bottom: 5px;
          color: #444;
        }
        .contract-item p {
          border: 1px solid #ddd;
          padding: 10px;
          background-color: #f9f9f9;
          min-height: 20px;
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="header">
          <div class="header-left">
            <h1>Transactional Analysis Contract</h1>
            <div class="client-info">
              <p><strong>Client:</strong> ${clientName}</p>
              <p><strong>Date:</strong> ${date}</p>
            </div>
          </div>
          <div class="header-right">
            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAC1UlEQVR4nO2ZTWhTQRDHf01qqKLFWluRIi2KH1gVD4o9KIoHFRHxIF4E9SJeRMSLePEgCIIXPyBaPQiiIIIHIaguKn4gilYrKKJYW6u1tVZrNOJhJmRNXt59+/btoZD8YAjZ2Z3578zu7M4bqKEGMlAHDAOvgR/AT2AMGAFOAtsLoF0RDACfAdfHpoCRIsiXFbuAaRnwC+A00CdaRj57ZI7TNe4u0F0wH20sBb7JoGeAw0BDytysPZfE5nIxJOxlAbBfPvsFnMsjdwJYLAJdCsOD6/eYXuAasEU8Mwk8Ak4AsxFkwLbMM8EV4PUcTVQRgYbEvZeA+4lpIBHPXAebVY4eZLGTH/K9Mc/GOWM80eBZ2DiBDTn0NQDvxObtnLZsEKcdGE5eyrP4TbH5mNNWv5xKYMNyjbPWx/XMTnuNfjCWEu7pM+tEO2LLUhGbrXsZdHeB4RyO2EATMCa2H5jtZUSUu3+pjPv2OcZZ7rRdBtZkNdjL6RQBMacxA5wE1jltzdKHpTmyHiVuiZ3fwA5nbrNsfqWpbFwRy++ABvk/T6LLZeBy1qD7JSVZ+XAeWCPrx9tOJrIdmPEksFvG9JisoNYkMO/NW0EWE4sO+jA3Yci7V+r93+jzkGuwfNJuhGttDTdNpJmKSGCJmScuBYvgLPNDXCbWIUXLQmAn3GfE4DPLF4VqQBp2m4xg63ot4WsDZgNaA8skRF455Wsu+4mCv5LVfC12Qgge9Jt+3Cr7qrVgIzAWuPnLlUKb1F9vA40vhEo3J4C7EZEG5aXwOdDbSrQJW6VAW6CKtVnC5mqgfBQMm0KWuFrM1dhm4HvVnpYL3TJwa0DzXRYkdapVjPXASwkZ95WWLbfbKs1LRNSBtoSfRYNVWhvNCVs1LVs/TkvJoG9NVBSrgbuSPH0C9mHQrJWLuaDSnSrBStc+kbCNHKp0KWuooUL4C0kZ9kKDi86wAAAAAElFTkSuQmCC" alt="Avenues Unlimited Counseling Center" />
            <p>Avenues Unlimited Counseling Center</p>
          </div>
        </div>
        <hr>
        
        <p class="description">
          This form documents the client's goals and commitments in therapy from a Transactional Analysis perspective.
        </p>
        
        <div class="contract-content">
          ${contractSections}
        </div>
        
        <div class="signature-section">
          <div class="signature-line">
            <span>Client Signature</span>
          </div>
          <div class="signature-line">
            <span>Therapist Signature</span>
          </div>
          <div class="signature-line">
            <span>Date</span>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Generate print content for Intake Assessment
 */
const generateIntakeAssessmentPrintContent = (note, clientName) => {
  const date = note.date || new Date().toLocaleDateString();
  
  // Format family substance abuse history
  let substanceAbuseFamilyHistoryText = 'None reported';
  if (note.substanceAbuseFamilyHistory && note.substanceAbuseFamilyHistory.length > 0) {
    substanceAbuseFamilyHistoryText = note.substanceAbuseFamilyHistory.join(', ');
    if (note.substanceAbuseFamilyHistory.includes('other') && note.substanceAbuseFamilyHistoryOther) {
      substanceAbuseFamilyHistoryText += ` (Other: ${note.substanceAbuseFamilyHistoryOther})`;
    }
  }
  
  // Format trauma history
  let traumaHistoryText = note.traumaHistory || 'No trauma history reported';
  if (note.traumaHistory === 'other' && note.traumaHistoryOther) {
    traumaHistoryText = `Other: ${note.traumaHistoryOther}`;
  }
  
  // Format relationship info
  let relationshipInfo = note.relationshipStatus || 'Not specified';
  if (note.relationshipStatus !== 'single, never married' && note.relationshipSatisfaction) {
    relationshipInfo += ` (Satisfaction: ${note.relationshipSatisfaction})`;
  }

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Intake Assessment - ${clientName}</title>
      <style>
        ${getPrintStyles()}
        .assessment-section {
          margin-bottom: 15px;
          padding-bottom: 10px;
        }
        .assessment-section h3 {
          color: #333;
          border-bottom: 1px solid #ccc;
          padding-bottom: 5px;
          margin-bottom: 5px;
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="header">
          <div class="header-left">
            <h1>Intake Assessment</h1>
            <div class="client-info">
              <p><strong>Client:</strong> ${clientName}</p>
              <p><strong>Date:</strong> ${date}</p>
            </div>
          </div>
          <div class="header-right">
            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAC1UlEQVR4nO2ZTWhTQRDHf01qqKLFWluRIi2KH1gVD4o9KIoHFRHxIF4E9SJeRMSLePEgCIIXPyBaPQiiIIIHIaguKn4gilYrKKJYW6u1tVZrNOJhJmRNXt59+/btoZD8YAjZ2Z3578zu7M4bqKEGMlAHDAOvgR/AT2AMGAFOAtsLoF0RDACfAdfHpoCRIsiXFbuAaRnwC+A00CdaRj57ZI7TNe4u0F0wH20sBb7JoGeAw0BDytysPZfE5nIxJOxlAbBfPvsFnMsjdwJYLAJdCsOD6/eYXuAasEU8Mwk8Ak4AsxFkwLbMM8EV4PUcTVQRgYbEvZeA+4lpIBHPXAebVY4eZLGTH/K9Mc/GOWM80eBZ2DiBDTn0NQDvxObtnLZsEKcdGE5eyrP4TbH5mNNWv5xKYMNyjbPWx/XMTnuNfjCWEu7pM+tEO2LLUhGbrXsZdHeB4RyO2EATMCa2H5jtZUSUu3+pjPv2OcZZ7rRdBtZkNdjL6RQBMacxA5wE1jltzdKHpTmyHiVuiZ3fwA5nbrNsfqWpbFwRy++ABvk/T6LLZeBy1qD7JSVZ+XAeWCPrx9tOJrIdmPEksFvG9JisoNYkMO/NW0EWE4sO+jA3Yci7V+r93+jzkGuwfNJuhGttDTdNpJmKSGCJmScuBYvgLPNDXCbWIUXLQmAn3GfE4DPLF4VqQBp2m4xg63ot4WsDZgNaA8skRF455Wsu+4mCv5LVfC12Qgge9Jt+3Cr7qrVgIzAWuPnLlUKb1F9vA40vhEo3J4C7EZEG5aXwOdDbSrQJW6VAW6CKtVnC5mqgfBQMm0KWuFrM1dhm4HvVnpYL3TJwa0DzXRYkdapVjPXASwkZ95WWLbfbKs1LRNSBtoSfRYNVWhvNCVs1LVs/TkvJoG9NVBSrgbuSPH0C9mHQrJWLuaDSnSrBStc+kbCNHKp0KWuooUL4C0kZ9kKDi86wAAAAAElFTkSuQmCC" alt="Avenues Unlimited Counseling Center" />
            <p>Avenues Unlimited Counseling Center</p>
          </div>
        </div>
        <hr>
        
        <div class="assessment-section">
          <h3>Presenting Problem(s), including current symptoms:</h3>
          <p>${note.presentingProblems || 'None recorded'}</p>
        </div>
        
        <div class="assessment-section">
          <h3>History: (family/medical)</h3>
          <p>${note.familyMedicalHistory || 'None recorded'}</p>
        </div>
        
        <div class="assessment-section">
          <h3>Trauma History:</h3>
          <p>${traumaHistoryText}</p>
        </div>
        
        <div class="assessment-section">
          <h3>Substance Abuse History:</h3>
          <p><strong>Family History:</strong> ${substanceAbuseFamilyHistoryText}</p>
          <p><strong>Substance Abuse Status for Client:</strong> ${note.substanceAbuseStatus || 'Not specified'}</p>
        </div>
        
        <div class="assessment-section">
          <h3>Previous Therapy Experiences:</h3>
          <p>${note.previousTherapyExperiences || 'No previous therapy experiences'}</p>
          ${note.previousTherapyNotes ? `<p><strong>Notes:</strong> ${note.previousTherapyNotes}</p>` : ''}
        </div>
        
        <div class="assessment-section">
          <h3>History/Current SI/HI/self-harm:</h3>
          <p>${note.selfHarmHistory || 'None recorded'}</p>
        </div>
        
        <div class="assessment-section">
          <h3>Current relationship status/satisfaction:</h3>
          <p>${relationshipInfo}</p>
        </div>
        
        <div class="assessment-section">
          <h3>Protective Factors/Support System:</h3>
          <p>${note.protectiveFactors || 'None recorded'}</p>
        </div>
        
        <div class="assessment-section">
          <h3>Strengths/Coping Skills:</h3>
          <p>${note.copingSkills || 'None recorded'}</p>
        </div>
        
        <div class="assessment-section">
          <h3>Mental Status Examination/Observations:</h3>
          <p>${note.mentalStatusExam || 'None recorded'}</p>
        </div>
        
        <div class="signature-section">
          <div class="signature-line">
            <span>Therapist Signature</span>
          </div>
          <div class="signature-line">
            <span>Date</span>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Generic print content for any other type of note
 */
const generateGenericPrintContent = (note, clientName, noteType) => {
  const date = note.date || new Date().toLocaleDateString();
  
  // Generate content based on fields in note
  let contentHtml = '';
  
  Object.entries(note).forEach(([key, value]) => {
    // Skip certain fields
    if (['date', 'type', 'id', 'clientId'].includes(key)) return;
    
    // Format the key for display
    const formattedKey = key
      .replace(/([A-Z])/g, ' $1') // Insert space before capital letters
      .replace(/^./, str => str.toUpperCase()); // Capitalize first letter
    
    // Handle simple or complex values
    if (typeof value === 'object' && value !== null) {
      contentHtml += `<div class="note-section"><h3>${formattedKey}</h3>`;
      Object.entries(value).forEach(([subKey, subValue]) => {
        const formattedSubKey = subKey
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase());
        contentHtml += `<p><strong>${formattedSubKey}:</strong> ${subValue || 'Not specified'}</p>`;
      });
      contentHtml += '</div>';
    } else {
      contentHtml += `
        <div class="note-section">
          <h3>${formattedKey}</h3>
          <p>${value || 'Not specified'}</p>
        </div>
      `;
    }
  });

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${noteType} - ${clientName}</title>
      <style>
        ${getPrintStyles()}
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="header">
          <div class="header-left">
            <h1>${noteType}</h1>
            <div class="client-info">
              <p><strong>Client:</strong> ${clientName}</p>
              <p><strong>Date:</strong> ${date}</p>
            </div>
          </div>
          <div class="header-right">
            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAAC1UlEQVR4nO2ZTWhTQRDHf01qqKLFWluRIi2KH1gVD4o9KIoHFRHxIF4E9SJeRMSLePEgCIIXPyBaPQiiIIIHIaguKn4gilYrKKJYW6u1tVZrNOJhJmRNXt59+/btoZD8YAjZ2Z3578zu7M4bqKEGMlAHDAOvgR/AT2AMGAFOAtsLoF0RDACfAdfHpoCRIsiXFbuAaRnwC+A00CdaRj57ZI7TNe4u0F0wH20sBb7JoGeAw0BDytysPZfE5nIxJOxlAbBfPvsFnMsjdwJYLAJdCsOD6/eYXuAasEU8Mwk8Ak4AsxFkwLbMM8EV4PUcTVQRgYbEvZeA+4lpIBHPXAebVY4eZLGTH/K9Mc/GOWM80eBZ2DiBDTn0NQDvxObtnLZsEKcdGE5eyrP4TbH5mNNWv5xKYMNyjbPWx/XMTnuNfjCWEu7pM+tEO2LLUhGbrXsZdHeB4RyO2EATMCa2H5jtZUSUu3+pjPv2OcZZ7rRdBtZkNdjL6RQBMacxA5wE1jltzdKHpTmyHiVuiZ3fwA5nbrNsfqWpbFwRy++ABvk/T6LLZeBy1qD7JSVZ+XAeWCPrx9tOJrIdmPEksFvG9JisoNYkMO/NW0EWE4sO+jA3Yci7V+r93+jzkGuwfNJuhGttDTdNpJmKSGCJmScuBYvgLPNDXCbWIUXLQmAn3GfE4DPLF4VqQBp2m4xg63ot4WsDZgNaA8skRF455Wsu+4mCv5LVfC12Qgge9Jt+3Cr7qrVgIzAWuPnLlUKb1F9vA40vhEo3J4C7EZEG5aXwOdDbSrQJW6VAW6CKtVnC5mqgfBQMm0KWuFrM1dhm4HvVnpYL3TJwa0DzXRYkdapVjPXASwkZ95WWLbfbKs1LRNSBtoSfRYNVWhvNCVs1LVs/TkvJoG9NVBSrgbuSPH0C9mHQrJWLuaDSnSrBStc+kbCNHKp0KWuooUL4C0kZ9kKDi86wAAAAAElFTkSuQmCC" alt="Avenues Unlimited Counseling Center" />
            <p>Avenues Unlimited Counseling Center</p>
          </div>
        </div>
        <hr>
        
        <div class="note-content">
          ${contentHtml}
        </div>
        
        <div class="signature-section">
          <div class="signature-line">
            <span>Therapist Signature</span>
          </div>
          <div class="signature-line">
            <span>Date</span>
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

/**
 * Common print styles for all documents
 */
const getPrintStyles = () => {
  return `
    body {
      font-family: Arial, sans-serif;
      margin: 0;
      padding: 0;
      color: #333;
      background-color: white;
    }
    .print-container {
      max-width: 800px;
      margin: 20px auto;
      padding: 20px;
    }
    .header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 20px;
    }
    .header-left {
      flex: 2;
    }
    .header-right {
      flex: 1;
      text-align: right;
    }
    .header-right img {
      height: 40px;
      width: auto;
    }
    .header-right p {
      margin-top: 5px;
      font-size: 12px;
    }
    h1 {
      color: #2c3e50;
      margin: 0 0 10px 0;
      font-size: 24px;
    }
    h2 {
      color: #3498db;
      font-size: 18px;
      margin: 15px 0 10px 0;
      border-bottom: 1px solid #eee;
      padding-bottom: 5px;
    }
    h3 {
      color: #555;
      font-size: 16px;
      margin: 12px 0 8px 0;
    }
    .client-info {
      margin-top: 5px;
    }
    .client-info p {
      margin: 2px 0;
      font-size: 14px;
    }
    .section, .note-section {
      margin-bottom: 20px;
    }
    .subsection {
      margin-left: 15px;
      margin-bottom: 10px;
    }
    hr {
      border: 0;
      height: 1px;
      background-color: #ddd;
      margin: 20px 0;
    }
    p {
      margin: 5px 0;
      line-height: 1.4;
    }
    .description {
      font-style: italic;
      margin-bottom: 20px;
      color: #666;
    }
    .signature-section {
      margin-top: 40px;
      page-break-inside: avoid;
    }
    .signature-line {
      border-top: 1px solid #000;
      margin-top: 30px;
      padding-top: 5px;
      width: 70%;
    }
    .signature-line span {
      font-size: 12px;
      color: #666;
    }
    @media print {
      body {
        margin: 0;
        padding: 0;
      }
      .print-container {
        width: 100%;
        margin: 0;
        padding: 15px;
        box-sizing: border-box;
      }
      @page {
        margin: 0.5cm;
      }
    }
  `;
};

export default {
  printNote
};